import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from 'utils/classMerge'

import { LoadingSpinner } from '../loading/spinner'

const buttonVariants = cva(
  'tw-focus-visible:outline-none tw-disabled:pointer-events-none tw-relative tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-font-roboto tw-transition-all disabled:tw-cursor-not-allowed disabled:tw-opacity-50 [&_span]:tw-font-roboto [&_svg]:tw-pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'tw-w-fit tw-border tw-border-solid tw-border-orange-700 tw-bg-orange-600 tw-bg-[linear-gradient(94.8deg,#FF5E29_50%,#FF9D00_100%)] tw-text-white tw-shadow-[0px_4px_10px_-4px_#FF5E2980,0px_15px_25px_-3px_#FF5E2940] [text-shadow:0px_1px_2px_#0000001A,0px_1px_3px_#0000001A] hover:tw-border-orange-800 hover:tw-bg-orange-600 hover:tw-bg-none active:tw-border-orange-800 active:tw-bg-orange-600 active:tw-bg-none',
      },
      size: {
        default:
          'tw-h-9 tw-min-h-9 tw-rounded-lg tw-px-4 tw-text-sm tw-font-bold',
        lg: 'tw-h-10 tw-min-h-10 tw-rounded-lg tw-px-4 tw-py-2 tw-text-base tw-font-bold',
        xl: 'tw-h-[52px] tw-min-h-[52px] tw-rounded-lg tw-px-5 tw-text-lg tw-font-black',
        '2xl':
          'tw-h-[68px] tw-min-h-[68px] tw-rounded-xl tw-px-8 tw-text-xl tw-font-black',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  textClassName?: string
}

const ButtonCTA = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading,
      children,
      disabled,
      textClassName = '',
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        {isLoading && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
            <LoadingSpinner />
          </div>
        )}
        <span
          className={cn(textClassName, 'tw-flex tw-items-center tw-gap-3', {
            'tw-pointer-events-none tw-opacity-0': isLoading,
          })}
        >
          {children}
        </span>
      </Comp>
    )
  },
)

ButtonCTA.displayName = 'ButtonCTA'

export { ButtonCTA, buttonVariants }
